import React, { useEffect, useState } from "react";
import Property from "../components/hostComponents/Property";
import HostNav from "../components/hostComponents/HostNav";
import { useDispatch, useSelector } from "react-redux";
import { getFlags, getHostProperties, getHostEarnings, getAllOrders } from "../store/thunks/hostActions";
import { useNavigate, Link } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import {
  updatePropertyReset,
  deletePropertyReset,
  addPropertyReset,
} from "../store/slices/hostSlices";
import request from "../utils/request";
import onboard from "../icons/onboard.svg";
import TutorialModal from "../components/TutorialModal";
import Calendly from "./Calendly";
import { isUpcomingOrder } from "../utils/orderStatus";
import ConciergePromoCard from "../components/hostComponents/ConciergePromoCard";

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();
const isStatus = (status, target) => normalizeStatus(status) === normalizeStatus(target);

// ─── Compact Analytics Bar ───────────────────────────────────────────────────
function AnalyticsBar({ activeProperties, totalEarnings, totalOrders, avgOrderValue }) {
  const navigate = useNavigate();

  const stats = [
    {
      label: 'Properties',
      value: activeProperties,
      onClick: () => navigate('/properties'),
    },
    {
      label: 'Earnings',
      value: `$${totalEarnings.toFixed(2)}`,
      onClick: () => navigate('/earnings'),
    },
    {
      label: 'Orders',
      value: totalOrders,
      onClick: () => navigate('/order-list'),
    },
    {
      label: 'Avg Value',
      value: `$${avgOrderValue.toFixed(2)}`,
      onClick: () => navigate('/earnings'),
    },
  ];

  return (
    <div className="bg-gray-100 rounded-2xl border border-gray-200 p-3 mb-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((stat, i) => (
          <button
            key={i}
            onClick={stat.onClick}
            className="group flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-white transition-all duration-200 text-left"
          >
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{stat.label}</p>
              <p className="text-sm font-bold text-gray-900 truncate">{stat.value}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Property List Row (compact list view) ───────────────────────────────────
function PropertyListRow({ property, data, rawDataList, earnings }) {
  const navigate = useNavigate();

  const propertyEarnings = earnings?.[property._id]?.earnings?.reduce((sum, amt) => sum + amt, 0) || 0;
  const orderCount = earnings?.[property._id]?.earnings?.length || 0;

  const formatAddress = (addr) => {
    if (!addr) return 'N/A';
    return [addr.line1, addr.city, addr.state].filter(Boolean).join(', ');
  };

  return (
    <div
      onClick={() => navigate(`/properties/${property._id}`)}
      className="group flex items-center justify-between px-5 py-3.5 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>

        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">
            {property.listingTitle || 'Untitled Property'}
          </p>
          <p className="text-xs text-gray-500 truncate">{formatAddress(property.address)}</p>
        </div>
      </div>

      <div className="flex items-center space-x-6 flex-shrink-0">
        <div className="hidden sm:block text-right">
          <p className="text-xs text-gray-400 font-medium">Orders</p>
          <p className="text-sm font-bold text-gray-900">{orderCount}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 font-medium">Earned</p>
          <p className="text-sm font-bold text-green-600">${propertyEarnings.toFixed(2)}</p>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-xs font-medium text-gray-500 hidden md:inline">Active</span>
        </div>
        <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Upcoming Orders Widget ────────────────────────────────────────────────────
function UpcomingOrdersWidget({ orders = [], receiveEntryInstructionsEmails = false }) {
  const navigate = useNavigate();
  const safeOrders = Array.isArray(orders) ? orders : [];
  
  const upcomingOrders = safeOrders
    .filter((order) => isUpcomingOrder(order))
    .sort((a, b) => new Date(a.inStockDate) - new Date(b.inStockDate));

  const ordersNeedingDetails = receiveEntryInstructionsEmails 
    ? upcomingOrders.filter(order => !order.hostOrderDetailsInputted).length 
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (upcomingOrders.length === 0) {
    return (
      <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-bold text-gray-900">Upcoming Orders</h3>
            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">0</span>
          </div>
          <Link to="/order-list" className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">View All</Link>
        </div>
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">No Upcoming Orders</h4>
          <p className="text-xs text-gray-500">Pending orders will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-bold text-gray-900">Upcoming Orders</h3>
          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">{upcomingOrders.length}</span>
        </div>
        <Link to="/order-list" className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">View All</Link>
      </div>

      {receiveEntryInstructionsEmails && ordersNeedingDetails > 0 && (
        <div className="mb-3 p-2.5 bg-red-50 rounded-xl border border-red-200">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-xs font-medium text-red-700">
              <span className="font-bold">{ordersNeedingDetails}</span> order{ordersNeedingDetails > 1 ? 's need' : ' needs'} entry instructions & delivery time
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {upcomingOrders.map((order, index) => {
          const needsDetails = receiveEntryInstructionsEmails && !order.hostOrderDetailsInputted;
          
          return (
            <div
              key={order._id || index}
              className={`flex items-center space-x-3 p-2.5 rounded-xl transition-colors cursor-pointer border ${
                needsDetails 
                  ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => navigate('/order-list')}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                needsDetails ? 'bg-red-100' : 'bg-gray-200'
              }`}>
                {needsDetails ? (
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-1.5">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
                  </p>
                  {needsDetails && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[9px] font-bold rounded uppercase">
                      Needs Info
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {order.property?.listingTitle || order.property?.address?.city || 'Property'}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-xs font-bold ${needsDetails ? 'text-red-600' : 'text-gray-900'}`}>
                  ${order.hostFee?.toFixed(2) || '0.00'}
                </p>
                <p className="text-[10px] text-gray-400">{formatDate(order.inStockDate || order.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Quick Actions Widget ────────────────────────────────────────────────────
function QuickActionsWidget() {
  const navigate = useNavigate();
  const { hostInfo } = useSelector((state) => state.host);

  const actions = [
    {
      title: "Add Property",
      description: "List a new property",
      icon: "M12 6v6m0 0v6m0-6h6m-6 0H6",
      link: "/add-property"
    },
    {
      title: "View Orders",
      description: "Check recent orders",
      icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
      link: "/order-list"
    },
    {
      title: "Earnings",
      description: "Track performance",
      icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1",
      link: "/earnings"
    },
    {
      title: "Settings",
      description: "Account & profile",
      icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      link: `/settings/${hostInfo?._id}`
    }
  ];

  return (
    <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
      <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.link)}
            className="group text-left p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all duration-200"
          >
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-300 transition-colors duration-200">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
              </svg>
            </div>
            <h4 className="font-bold text-xs text-gray-900 mb-0.5">{action.title}</h4>
            <p className="text-gray-500 text-[10px]">{action.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── View Toggle ─────────────────────────────────────────────────────────────
function ViewToggle({ viewMode, setViewMode }) {
  return (
    <div className="flex items-center bg-gray-100 rounded-xl p-1 border border-gray-200">
      <button
        onClick={() => setViewMode('grid')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('list')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main Host Screen ────────────────────────────────────────────────────────
const HostScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [onBoardLoading, setOnBoardLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [errorOnBorad, setErrorOnBoard] = useState();
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [gateLoading, setGateLoading] = useState(true);
  const [onboardingGate, setOnboardingGate] = useState({
    shouldShowTutorial: false,
    shouldShowOnboardingCall: false,
    isOnBoarded: false,
    activePropertyCount: 0,
    hasCompletedTutorial: false,
    tutorialCompletedAt: null,
  });
  const [rawDataList, setRawDataList] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [viewMode, setViewMode] = useState("grid");

  const handleClick = async () => {
    setOnBoardLoading(true);
    try {
      const { data } = await request.get("/stripe/get-onboarding-link");
      window.location.href = data.url;
    } catch (error) {
      setErrorOnBoard(error.response.data.msg);
    }
  };

  const { hostInfo } = useSelector((state) => state.host);
  const hostDetails = useSelector((state) => state.hostDetails.Info);
  const isOnBoarded = hostDetails?.isOnBoarded;

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchData = async () => {
      try {
        const response = await request.get(
          `properties/host/${hostInfo._id}/commission`
        );
        setData(response.data.commission);
      } catch (error) {
        console.error("An error occurred:", error);
        setData([]);
      }
    };

    const fetchOrders = async () => {
      try {
        const action = await dispatch(getAllOrders(hostInfo._id));
        const fetchedOrders = action?.payload?.orders;
        setAllOrders(Array.isArray(fetchedOrders) ? fetchedOrders : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setAllOrders([]);
      }
    };

    if (hostInfo._id) {
      setIsLoading(true);
      dispatch(getFlags(hostInfo._id));
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      fetchData();
      fetchOrders();
      setIsLoading(false);
    }
  }, [hostInfo?._id, dispatch]);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchOnboardingGate = async () => {
      setGateLoading(true);
      try {
        const { data } = await request.get("/hosts/onboarding-gate", {
          withCredentials: true,
        });
        const gate = data?.gate || {};
        setOnboardingGate({
          shouldShowTutorial: Boolean(gate.shouldShowTutorial),
          shouldShowOnboardingCall: Boolean(gate.shouldShowOnboardingCall),
          isOnBoarded: Boolean(gate.isOnBoarded),
          activePropertyCount: Number(gate.activePropertyCount || 0),
          hasCompletedTutorial: Boolean(gate.hasCompletedTutorial),
          tutorialCompletedAt: gate.tutorialCompletedAt || null,
        });
        setShowTutorial(Boolean(gate.shouldShowTutorial));
      } catch (error) {
        console.error("Error fetching onboarding gate:", error);
        setOnboardingGate({
          shouldShowTutorial: false,
          shouldShowOnboardingCall: false,
          isOnBoarded: false,
          activePropertyCount: 0,
          hasCompletedTutorial: false,
          tutorialCompletedAt: null,
        });
        setShowTutorial(false);
      } finally {
        setGateLoading(false);
      }
    };

    fetchOnboardingGate();
  }, [hostInfo?._id]);

  const { loading: loadingProperties, properties, error } = useSelector(
    (state) => state.hostProperties
  );

  const { earnings } = useSelector((state) => state.hostEarnings);
  
  const { receiveEntryInstructionsEmails } = useSelector((state) => state.hostFlags);

  const { success: successUpdate, message: messsageupdate } = useSelector(
    (state) => state.propertyUpdate
  );
  const { success: successDelete, message: messageDelete } = useSelector(
    (state) => state.propertyDelete
  );
  const {
    success: successAddProperty,
    msg: messageAddProperty,
  } = useSelector((state) => state.addProperty);

  useEffect(() => {
    if ((successUpdate || successDelete) && hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
    }
  }, [successUpdate, successDelete, dispatch, hostInfo?._id]);

  useEffect(() => {
    if (successUpdate || successDelete || successAddProperty) {
      setTimeout(() => {
        successUpdate && dispatch(updatePropertyReset());
        successDelete && dispatch(deletePropertyReset());
        successAddProperty && dispatch(addPropertyReset());
      }, 2000);
    }
  }, [successAddProperty, successUpdate, successDelete, dispatch]);

  const handleNext = () => setTutorialStep((prev) => prev + 1);
  const handleBack = () => setTutorialStep((prev) => prev - 1);
  const handleClose = async () => {
    try {
      await request.post(
        "/hosts/onboarding-gate/complete",
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error completing tutorial gate:", error);
    }
    setShowTutorial(false);
    setOnboardingGate((prev) => ({
      ...prev,
      shouldShowTutorial: false,
      shouldShowOnboardingCall: false,
      hasCompletedTutorial: true,
      tutorialCompletedAt: new Date().toISOString(),
    }));
  };

  let propertyList = Array.isArray(properties) ? properties : [];
  if (search.length !== 0) {
    propertyList = propertyList.filter((property) => {
      try {
        const addressString = `${property?.address?.line1 || ""} ${property?.address?.line2 || ""}, ${property?.address?.city || ""}, ${property?.address?.state || ""} ${property?.address?.zipCode || ""}, United States`
          .toLowerCase();
        return addressString.includes(search.toLowerCase());
      } catch (error) {
        console.error("Error filtering property:", error);
        return false;
      }
    });
  }

  const activeProperties = Array.isArray(properties) ? properties.filter(p => !p.isDeleted) : [];
  const uniqueOrders = Array.from(
    new Map(
      (Array.isArray(allOrders) ? allOrders : []).map((order) => [
        order?._id || order?.uniqueId,
        order,
      ])
    ).values()
  );

  const finalizedOrders = uniqueOrders.filter((order) => !isStatus(order?.status, "created"));
  const earnedOrders = finalizedOrders.filter(
    (order) => isStatus(order?.status, "completed") || isStatus(order?.status, "paymentCompleted")
  );

  const totalOrders = finalizedOrders.length;
  const totalEarnings = earnedOrders.reduce((sum, order) => sum + (Number(order?.hostFee) || 0), 0);
  const avgOrderValue = earnedOrders.length > 0 ? totalEarnings / earnedOrders.length : 0;

  if (loadingProperties || isLoading || gateLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#1a1a1a' }} />
          <p className="mt-4 text-gray-500 font-semibold">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (onboardingGate.shouldShowOnboardingCall) {
    return (
      <div className="min-h-screen bg-white">
        {showTutorial && (
          <TutorialModal
            step={tutorialStep}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleClose}
          />
        )}
        <div className="max-w-3xl mx-auto px-6 py-8">
          <HostNav searchBar={false} setSearch={setSearch} />

          <div className="mt-6 space-y-6">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-900">
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Book Your Onboarding Call</h2>
              <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                Pick a time that works for you. We'll walk you through setup, answer questions, and get you earning.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-100 p-6">
              <Calendly />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <HostNav searchBar={true} setSearch={setSearch} />

        {/* Success/Error Messages */}
        {successAddProperty && (
          <Alert severity="success" className="mb-3">{messageAddProperty}</Alert>
        )}
        {successUpdate && (
          <Alert severity="success" className="mb-3">{messsageupdate}</Alert>
        )}
        {successDelete && (
          <Alert severity="warning" className="mb-3">{messageDelete}</Alert>
        )}

        {isOnBoarded === false ? (
          <div className="bg-gray-100 rounded-2xl p-6 border border-gray-200">
            {errorOnBorad && (
              <Alert severity="error" className="mb-4">{errorOnBorad}</Alert>
            )}
            <div className="text-center max-w-md mx-auto">
              <div className="mb-4">
                <img src={onboard} alt="Onboarding" className="mx-auto h-20 w-auto" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Complete Your Setup</h3>
              <p className="text-gray-500 mb-4">
                Cannot add properties until you have set up your Stripe Account
              </p>
              <button
                onClick={handleClick}
                disabled={onBoardLoading}
                className="w-full bg-gray-900 text-white font-bold py-4 px-6 rounded-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {onBoardLoading ? (
                  <>
                    <CircularProgress color="inherit" className="mr-2" size={20} />
                    Connecting...
                  </>
                ) : (
                  "Link Stripe Account"
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {error && <Alert severity="error">{error}</Alert>}

            <ConciergePromoCard hostId={hostInfo._id} />

            <AnalyticsBar
              activeProperties={activeProperties.length}
              totalEarnings={totalEarnings}
              totalOrders={totalOrders}
              avgOrderValue={avgOrderValue}
            />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <UpcomingOrdersWidget orders={allOrders} receiveEntryInstructionsEmails={receiveEntryInstructionsEmails} />
              <QuickActionsWidget />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostScreen;
